import { set } from "automate-redux";
import printJS from "print-js";
import { getSelectedFcYearDetails, getSelectedFcYearId, getSelectedOrgId } from "./user-management";
import { axios } from "../client";
import store from "../store";
import { getFromToDatesFromPeriod, notifyError, notifySuccess, printOrSaveBlobFromServer, uploadFile } from "../utils";

export const fetchAccountGroupsLedgerSummary = async (from, to, closingStockValue) => {
  const state = store.getState();
  const params = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    from,
    to,
    closingStockValue
  };
  const { status, data } = await axios.get("/v1/reports/getAccountGroupsLedgerSummary", { params });
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const fetchAccountsLedgerSummary = async (accountGroupId, from, to) => {
  const state = store.getState();
  const params = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    agm_id: accountGroupId,
    from,
    to,
  };
  const { status, data } = await axios.get("/v1/reports/getAccountsLedgerSummary", { params });
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const fetchAccountMonthWiseLedgerSummary = async (accountId, fromDate, toDate) => {
  const state = store.getState();
  const params = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    from: fromDate,
    to: toDate,
    acm_id: accountId,
  };
  const { status, data } = await axios.get("/v1/reports/getAccountMonthWiseLedgerSummary", {
    params,
  });
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const fetchAccountLedger = async (acmId, from, to, bankStatementMode, org, refAcmId) => {
  const state = store.getState();
  const params = { org_id: getSelectedOrgId(state), acm_id: acmId, ref_acm_id: refAcmId, from, to, bankStatementMode, org };
  const { status, data } = await axios.get("/v1/reports/getAccountLedger", { params });
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const fetchOutstandingSummary = async (options) => {
  const state = store.getState();
  const params = { org_id: getSelectedOrgId(state), fy_id: getSelectedFcYearId(state), options };
  const { status, data } = await axios.get("/v1/reports/getOutstandingSummary", { params });
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const printOrSaveOutstandingSummary = async (options, action, fileType) => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    file_type: fileType,
    options,
  };
  const response = await axios.post("/v1/reports/downloadOutstandingSummary", reqBody, {
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, action);
};

export const whatsappOutstanding = async (options, templateName, sendTo, customNumber, sendPartyWiseOutstanding) => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    options,
    templateName,
    sendTo,
    customNumber,
    sendPartyWiseOutstanding
  };
  const response = await axios.post("/v1/reports/whatsappOutstanding", reqBody);
  if (response.status !== 200) {
    throw response.data;
  }
  if (response.data?.successCount > 0) {
    notifySuccess(`Sent ${response.data.successCount} message successfully`);
  }
  if (response.data?.failedRequests?.length > 0) {
    notifyError(`Error sending ${response.data?.failedRequests?.length} message`, '', response.data?.failedRequests);
  }
  return response.data;
};

export const whatsappReports = async (reportId, reportOptions, options) => {
  const state = store.getState();
  const data = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    report_id: reportId,
    report_options: reportOptions,
    options,
  };
  const response = await axios.post("/v1/whatsappReports", data);
  if (response.status !== 200) {
    throw response.data;
  }
  if (response.data?.successCount > 0) {
    notifySuccess(`Sent ${response.data.successCount} message successfully`);
  }
  if (response.data?.failedRequests?.length > 0) {
    notifyError(`Error sending ${response.data?.failedRequests?.length} message`, '', response.data?.failedRequests);
  }
  return response.data;
};

export const emailReports = async (reportId, reportOptions, options) => {
  const state = store.getState();
  const data = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    report_id: reportId,
    report_options: reportOptions,
    options,
  };
  const response = await axios.post("/v1/emailReports", data);
  if (response.status !== 200) {
    throw response.data;
  }
};

export const fetchOrdersReport = async (options) => {
  const state = store.getState();
  const params = { org_id: getSelectedOrgId(state), options };
  const { status, data } = await axios.get("/v1/reports/orders/getOrdersReport", { params });
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const fetchOrderDetails = async (orderId, type = 'SLO') => {
  const state = store.getState();
  const params = { org_id: getSelectedOrgId(state), ord_id: orderId, type };
  const { status, data } = await axios.get("/v1/reports/orders/getOrderDetails", { params });
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const fetchItemRateList = async (options) => {
  const state = store.getState();
  const params = { org_id: getSelectedOrgId(state), options };
  const { status, data } = await axios.get("/v1/reports/getItemRateList", { params });
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const fetchListForBankSlipPrinting = async (date, bookId, type) => {
  const state = store.getState();
  const params = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    date,
    book_id: bookId,
    type,
  };
  const { status, data } = await axios.get("/v1/reports/getListForBankSlipPrinting", { params });
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const printBankSlip = async (date, bookId, type, trnIds, format) => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    date,
    book_id: bookId,
    type,
    ids: trnIds,
    format
  };
  const response = await axios.post("/v1/reports/downloadBankSlip", reqBody, {
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, "print");
};

export const fetchBankReconData = async (bookId, from, to, status) => {
  const state = store.getState();
  const params = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    book_id: bookId,
    from,
    to,
    status
  };
  const { status: resStatus, data } = await axios.get("/v1/reports/getBankReconData", { params });
  if (resStatus !== 200) {
    throw data;
  }
  return data;
};

export const printOrSaveBankRecon = async (bookId, from, to, action = "print", fileType = "pdf") => {
  const state = store.getState();
  const params = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    book_id: bookId,
    from,
    to,
    file_type: fileType
  };
  const response = await axios.get("/v1/reports/downloadBankRecon", {
    params,
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, action);
};

export const printOrSaveBankClearing = async (bookId, from, to, action = "print", fileType = "pdf") => {
  const state = store.getState();
  const params = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    book_id: bookId,
    from,
    to,
    file_type: fileType
  };
  const response = await axios.get("/v1/reports/downloadBankClearing", {
    params,
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, action);
};

export const updateBankRecon = async (acmId, bankReconData) => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    acm_id: acmId,
    data: bankReconData
  };
  const { status, data } = await axios.post("/v1/reports/updateBankRecon", reqBody);
  if (status !== 200) {
    throw data;
  }
  return;
};

export const updateBankBalLock = async (acmId, date) => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    acm_id: acmId,
    date,
  };
  const { status, data } = await axios.post("/v1/updateBankBalLock", reqBody);
  if (status !== 200) {
    throw data;
  }
  return;
};

// ── Auto BRS ──

export const previewBankStatement = async (acmId, file, password) => {
  const state = store.getState();
  return uploadFile("/v1/reports/autoBrs/previewStatement", file, {
    org_id: getSelectedOrgId(state),
    acm_id: acmId,
    password: password || "",
  });
};

export const importBankStatement = async (acmId, file, password, mode) => {
  const state = store.getState();
  return uploadFile("/v1/reports/autoBrs/importStatement", file, {
    org_id: getSelectedOrgId(state),
    acm_id: acmId,
    password: password || "",
    mode,
  });
};

export const fetchAutoBrsData = async (acmId, from, to) => {
  const state = store.getState();
  const params = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    acm_id: acmId,
    from,
    to,
  };
  const { status, data } = await axios.get("/v1/reports/autoBrs/getData", { params });
  if (status !== 200) {
    throw data;
  }
  return data;
};

/** The period to open Auto BRS with: { kind: OPEN_STATEMENT | NEXT | DEFAULT, from, to (null = today), lockDate } */
export const fetchAutoBrsSuggestedPeriod = async (acmId) => {
  const state = store.getState();
  const params = { org_id: getSelectedOrgId(state), acm_id: acmId };
  const { status, data } = await axios.get("/v1/reports/autoBrs/getSuggestedPeriod", { params });
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const fetchBankStatementUploads = async (acmId) => {
  const state = store.getState();
  const params = { org_id: getSelectedOrgId(state), acm_id: acmId };
  const { status, data } = await axios.get("/v1/reports/autoBrs/getUploads", { params });
  if (status !== 200) {
    throw data;
  }
  return data.uploads;
};

/** What to prefill when creating a book entry from a Missing in Book line: { type: RCP | PYM | RCQ, date, amount, party, chqno, remarks, original, note } */
export const fetchAutoBrsEntryContext = async (acmId, stmtLineId) => {
  const state = store.getState();
  const params = { org_id: getSelectedOrgId(state), acm_id: acmId, stmt_line_id: stmtLineId };
  const { status, data } = await axios.get("/v1/reports/autoBrs/getEntryContext", { params });
  if (status !== 200) {
    throw data;
  }
  return data;
};

const postAutoBrs = async (path, body) => {
  const state = store.getState();
  const reqBody = { org_id: getSelectedOrgId(state), fy_id: getSelectedFcYearId(state), ...body };
  const { status, data } = await axios.post(`/v1/reports/autoBrs/${path}`, reqBody);
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const deleteBankStatementUpload = (uploadId) => postAutoBrs("deleteUpload", { upload_id: uploadId });

/** groups: [{ kind: "MATCH" | "REVERSAL", stmt_line_ids, trn_ids }] */
/** groups[].remember: also remember the line's identity as the entry's party; extra.offer_remember: return what could be */
export const acceptAutoBrsMatches = (acmId, groups, extra = {}) => postAutoBrs("accept", { acm_id: acmId, groups, ...extra });

export const unreconcileAutoBrs = (acmId, groupIds) => postAutoBrs("unreconcile", { acm_id: acmId, group_ids: groupIds });

/** anchors: [{ stmt_line_id } | { trn_id }] */
export const rejectAutoBrs = (acmId, anchors) => postAutoBrs("reject", { acm_id: acmId, anchors });

export const restoreAutoBrs = (acmId, anchors) => postAutoBrs("restore", { acm_id: acmId, anchors });

export const ignoreAutoBrsLines = (acmId, lineIds) => postAutoBrs("ignoreLines", { acm_id: acmId, stmt_line_ids: lineIds });

/** "Remember for next time": the bank line's identity (account no. / UPI id / name) is this party */
export const rememberAutoBrsAlias = (acmId, lineId, partyId, replace = false) =>
  postAutoBrs("remember", { acm_id: acmId, stmt_line_id: lineId, party_id: partyId, replace });

export const forgetAutoBrsAliases = (acmId, ids) => postAutoBrs("forget", { acm_id: acmId, ids });

/** Multi-line Create entry: the prefilled entry of each line (as fetchAutoBrsEntryContext) plus what "Remember" would store */
export const fetchAutoBrsEntryContexts = (acmId, lineIds) => postAutoBrs("getEntryContexts", { acm_id: acmId, stmt_line_ids: lineIds });

export const fetchAutoBrsAliases = async () => {
  const state = store.getState();
  const { status, data } = await axios.get("/v1/reports/autoBrs/getAliases", { params: { org_id: getSelectedOrgId(state) } });
  if (status !== 200) {
    throw data;
  }
  return data;
};

/** One-time setup for books tallied by changing voucher dates: entries up to uptoDate are marked cleared on their own date */
export const applyAutoBrsOpeningSetup = (acmId, uptoDate, lock) =>
  postAutoBrs("applyOpeningSetup", { acm_id: acmId, upto_date: uptoDate, lock });

export const printOrSaveTrnsInterestRegister = async (data, options, action) => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    options,
    data,
  };
  const response = await axios.post("/v1/reports/downloadTrnsInterestRegister", reqBody, {
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, action);
};

export const printOrSaveAccountGroupsLedgerSummary = async (reportData, action, fileType) => {
  const state = store.getState()
  const queryParams = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    ...reportData,
    file_type: fileType,
  };
  const response = await axios.get("/v1/reports/downloadAccountGroupsLedgerSummary", {
    params: queryParams,
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, action);
};

export const printOrSaveAccountsLedgerSummary = async (reportData, action, fileType) => {
  const state = store.getState();
  const queryParams = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    ...reportData,
    file_type: fileType,
  };
  const response = await axios.get("/v1/reports/downloadAccountsLedgerSummary", {
    params: queryParams,
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, action);
};

export const printOrSaveAccountMonthWiseLedgerSummary = async (reportData, action, fileType) => {
  const state = store.getState();
  const queryParams = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    ...reportData,
    file_type: fileType,
  };
  const response = await axios.get("/v1/reports/downloadAccountMonthWiseLedgerSummary", {
    params: queryParams,
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, action);
};

export const exportGSTR1Report = async (from, to, enableAmmendments, action = "save", callback) => {
  const queryParams = {
    from,
    to,
    org_id: getSelectedOrgId(store.getState()),
    enableAmmendments
  };
  const response = await axios.get("/v1/reports/gstr/gstr1/download", {
    params: queryParams,
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, action, callback);
};

export const getGSTR1Data = async (from, to, enableAmmendments) => {
  const state = store.getState();
  const queryParams = {
    from,
    to,
    enableAmmendments,
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
  };
  const response = await axios.get("/v1/reports/gstr/gstr1/data", { params: queryParams });
  if (response.status !== 200) {
    throw response.data;
  }
  return Promise.resolve(response.data);
};

export const checkGSTR1Errors = async (from, to, enableAmmendments) => {
  const queryParams = {
    from,
    to,
    org_id: getSelectedOrgId(store.getState()),
    fy_id: getSelectedFcYearId(store.getState()),
    enableAmmendments,
  };
  const response = await axios.get("/v1/reports/gstr/gstr1/checkErrors", { params: queryParams });
  if (response.status !== 200) {
    throw response.data;
  }
  return Promise.resolve(response.data.errors);
};

export const markGSTR1AsPrepared = async (from, to, time) => {
  const reqBody = {
    from,
    to,
    org_id: getSelectedOrgId(store.getState()),
    time,
  };
  const response = await axios.post("/v1/reports/gstr/gstr1/markAsPrepared", reqBody);
  if (response.status !== 200) {
    throw response.data;
  }
  return Promise.resolve(response.data);
};

export const downloadGSTR2AReport = async (from, to) => {
  const queryParams = {
    from,
    to,
    org_id: getSelectedOrgId(store.getState()),
  };
  const response = await axios.get("/v1/reports/gstr/gstr2a/download", {
    params: queryParams,
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, "save");
};

export const fetchGSTR3BReport = async (from, to, enableAmmendments, show2bErrors) => {
  const queryParams = {
    from,
    to,
    enableAmmendments,
    org_id: getSelectedOrgId(store.getState()),
    show2bErrors,
  };
  const response = await axios.get("/v1/reports/gstr/gstr3b/data", { params: queryParams });
  if (response.status !== 200) {
    throw response.data;
  }
  return Promise.resolve(response.data);
};

export const loadGSTR2BReport = (fromDate, toDate, asOnDate, showPrevErrors, errorFromDate, errorAmt) => async (dispatch, getState) => {
  const state = getState();

  const params = {
    org_id: getSelectedOrgId(state),
    from_date: fromDate,
    to_date: toDate,
    as_on_date: asOnDate,
    show_prev_errors: showPrevErrors,
    error_from_date: errorFromDate,
    errorAmt,
  };
  const { status, data } = await axios.get("/v1/reports/gstr/gstr2b/data", { params });
  if (status !== 200) {
    throw data;
  }

  dispatch(set("gstr2bReport", data));
};

export const reloadGSTR2BReport = () => async (dispatch, getState) => {
  const state = getState();
  const { from_date, to_date, as_on_date, show_prev_errors, error_from_date, errorAmt } = state.gstr2bReportFormValues || {};
  await dispatch(loadGSTR2BReport(from_date, to_date, as_on_date, show_prev_errors, error_from_date, errorAmt));
};

export const unclaimGstr2bData = async () => {
  const state = store.getState();
  const { from_date, to_date } = state.gstr2bReportFormValues || {};
  const reqBody = {
    org_id: getSelectedOrgId(state),
    from_date,
    to_date,
  };

  const response = await axios.post("/v1/reports/gstr/gstr2b/unclaim2b", reqBody);
  if (response.status !== 200) {
    throw response.data;
  }
};

export const downloadGstr2bReport = async (gstno, type, ids, fileType, action = "save", callback) => {
  const state = store.getState();
  const { from_date, to_date, as_on_date, show_prev_errors, error_from_date, errorAmt } = state.gstr2bReportFormValues || {};
  const reqBody = {
    org_id: getSelectedOrgId(state),
    gstno,
    type,
    from_date,
    to_date,
    as_on_date,
    show_prev_errors,
    error_from_date,
    errorAmt,
    file_type: fileType,
    ids,
  };

  const response = await axios.post("/v1/reports/gstr/gstr2b/download2b", reqBody, { responseType: "arraybuffer" });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, action, callback);
};

export const printGstr2bReport = async (gstno, type, ids) => {
  const state = store.getState();
  const { from_date, to_date, as_on_date, show_prev_errors, error_from_date, errorAmt } = state.gstr2bReportFormValues || {};
  const reqBody = {
    org_id: getSelectedOrgId(state),
    gstno,
    type,
    from_date,
    to_date,
    as_on_date,
    show_prev_errors,
    error_from_date,
    errorAmt,
    ids,
  };

  const response = await axios.post("/v1/reports/gstr/gstr2b/download2b", reqBody, { responseType: "arraybuffer" });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }

  const pdfFile = new Blob([response.data], { type: "application/pdf" });
  const pdfUrl = window.URL.createObjectURL(pdfFile);
  printJS(pdfUrl);
};

export const reject2BRecords = async (records, remarks) => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    records,
    remarks
  };

  const response = await axios.post("/v1/reports/gstr/gstr2b/reject2BRecords", reqBody);
  if (response.status !== 200) {
    throw response.data;
  }
};

export const unreject2BRecords = async (records) => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    records
  };

  const response = await axios.post("/v1/reports/gstr/gstr2b/unreject2BRecords", reqBody);
  if (response.status !== 200) {
    throw response.data;
  }
};

export const deletePortal2BRecords = async (records) => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    records
  };

  const response = await axios.post("/v1/reports/gstr/gstr2b/deletePortal2BRecords", reqBody);
  if (response.status !== 200) {
    throw response.data;
  }
};


export const createPortal2BRecords = async (records, period) => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    period,
    records
  };

  const response = await axios.post("/v1/reports/gstr/gstr2b/createPortal2BRecords", reqBody);
  if (response.status !== 200) {
    throw response.data;
  }
};

export const downloadGstr3bReport = async (from, to, enableAmmendments, itcSectionType, show2bErrors, fileType, action = "save", callback) => {
  const state = store.getState();
  const queryParams = {
    org_id: getSelectedOrgId(state),
    file_type: fileType,
    from,
    to,
    enableAmmendments,
    itcSectionType,
    show2bErrors,
  };

  const response = await axios.get("/v1/reports/gstr/gstr3b/download3b", {
    params: queryParams,
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, action, callback);
};

export const printGstr3bReport = async (from, to, enableAmmendments, itcSectionType, show2bErrors) => {
  const state = store.getState();
  const queryParams = {
    org_id: getSelectedOrgId(state),
    from,
    to,
    enableAmmendments,
    itcSectionType,
    show2bErrors,
  };

  const response = await axios.get("/v1/reports/gstr/gstr3b/download3b", {
    params: queryParams,
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }

  const pdfFile = new Blob([response.data], { type: "application/pdf" });
  const pdfUrl = window.URL.createObjectURL(pdfFile);
  printJS(pdfUrl);
};

export const downloadGstr9Report = async (from, to) => {
  const state = store.getState();
  const queryParams = {
    org_id: getSelectedOrgId(state),
    from,
    to,
  };

  const response = await axios.get("/v1/reports/gstr/gstr9/download", {
    params: queryParams,
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, "save");
};

export const printOrSaveTcsSummary = async (options, action, fileType) => {
  const state = store.getState();
  const queryParams = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    file_type: fileType,
    options,
  };
  const response = await axios.get("/v1/reports/tcs/downloadSummary", {
    params: queryParams,
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, action);
};

export const printOrSaveTdsTcsChallans = async (ids, action) => {
  const state = store.getState();
  const queryParams = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    ids,
  };
  const response = await axios.get("/v1/reports/tds-tcs/downloadChallans", {
    params: queryParams,
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, action);
};

export const downloadTdsTcsReturnFilingReport = async (period, type) => {
  const state = store.getState();
  const fcYearDetails = getSelectedFcYearDetails(state);
  const { fromDate, toDate } = getFromToDatesFromPeriod(
    { type: period },
    fcYearDetails.date1,
    fcYearDetails.date2
  );
  const queryParams = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    from: fromDate,
    to: toDate,
    type,
  };
  const response = await axios.get("/v1/reports/tds-tcs/downloadReturnFilingReport", {
    params: queryParams,
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, "save");
};

export const fetchTcsSummary = async (options) => {
  const state = store.getState();
  const params = { org_id: getSelectedOrgId(state), options };
  const { status, data } = await axios.get("/v1/reports/tcs/getSummary", { params });
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const fetchPendingTdsTcsChallans = async () => {
  const state = store.getState();
  const params = { org_id: getSelectedOrgId(state), fy_id: getSelectedFcYearId(state) };
  const { status, data } = await axios.get("/v1/reports/tds-tcs/getPendingChallans", { params });
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const fetchTdsTcsChallans = async () => {
  const state = store.getState();
  const params = { org_id: getSelectedOrgId(state), fy_id: getSelectedFcYearId(state) };
  const { status, data } = await axios.get("/v1/reports/tds-tcs/getChallans", { params });
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const createTdsTcsChallan = async (challan) => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    challan,
  };
  const { status, data } = await axios.post("/v1/reports/tds-tcs/createChallan", reqBody);
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const updateTdsTcsChallan = async (request) => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    ...request,
  };
  const { status, data } = await axios.post("/v1/reports/tds-tcs/updateChallan", reqBody);
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const deleteTdsTcsChallans = async (ids) => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    ids,
  };
  const { status, data } = await axios.post("/v1/reports/tds-tcs/deleteChallans", reqBody);
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const printOrSaveFinalLedgerReport = async (reportData, action, fileType, callback) => {
  const reqBody = {
    org_id: getSelectedOrgId(store.getState()),
    ...reportData,
    file_type: fileType
  };
  const response = await axios.post("/v1/reports/downloadFinalLedger", reqBody, {
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, action, callback);
};

export const fetchLedgerInterestReport = async (values) => {
  const state = store.getState();
  const reqBody = { org_id: getSelectedOrgId(state), ...values };
  const { status, data } = await axios.post("/v1/reports/getLedgerInterestReport", reqBody);
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const downloadLedgerInterestReport = async (values, fileType) => {
  const state = store.getState();
  const reqBody = { org_id: getSelectedOrgId(state), ...values, file_type: fileType };
  const response = await axios.post("/v1/reports/downloadLedgerInterestReport", reqBody, { responseType: "arraybuffer" });

  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, "save");
};

export const fetchDepreciationReport = async (values) => {
  const state = store.getState();
  const params = { org_id: getSelectedOrgId(state), fy_id: getSelectedFcYearId(state), ...values };
  const { status, data } = await axios.get("/v1/reports/getDepreciationReport", { params });
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const fetchLedgerOutstandingMismatch = async () => {
  const state = store.getState();
  const reqBody = { org_id: getSelectedOrgId(state), fy_id: getSelectedFcYearId(state) };
  const { status, data } = await axios.post("/v1/reports/getLedgerOutstandingMismatchReport", reqBody);
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const fetchLedgerOutstandingMismatchDetails = async (id) => {
  const state = store.getState();
  const reqBody = { org_id: getSelectedOrgId(state), fy_id: getSelectedFcYearId(state), id };
  const { status, data } = await axios.post("/v1/reports/getLedgerOutstandingMismatchDetails", reqBody);
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const printOrSaveLedgerOutstandingMismatch = async (ids, action, fileType) => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    ids,
    file_type: fileType,
  };
  const response = await axios.post("/v1/reports/downloadLedgerOutstandingMismatchReport", reqBody, { responseType: "arraybuffer" });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, action);
};

export const fetchReport = async (params) => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    ...params,
  };
  const response = await axios.post("/v1/reports/generateReport", reqBody);
  if (response.status !== 200) {
    throw response.data;
  }
  return Promise.resolve(response.data);
};

export const downloadReport = async (params, fileType, action = "save", callback) => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    file_type: fileType,
    ...params,
  };

  const response = await axios.post("/v1/reports/downloadReport", reqBody, {
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, action, callback);
};

export const printReport = async (params) => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    format: "pdf",
    ...params,
  };

  const response = await axios.post("/v1/reports/downloadReport", reqBody, {
    responseType: "arraybuffer",
  });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }

  const pdfFile = new Blob([response.data], { type: "application/pdf" });
  const pdfUrl = window.URL.createObjectURL(pdfFile);
  printJS(pdfUrl);
};


export const fetchLotCostingReport = async (ids) => {
  const state = store.getState();
  const params = { org_id: getSelectedOrgId(state), ids };
  const { status, data } = await axios.get("/v1/reports/getLotCostingReport", { params });
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const fetchJobCosting = async (orgId, request) => {
  const state = store.getState();
  const reqBody = { org_id: orgId || getSelectedOrgId(state), ...request };
  const { status, data } = await axios.post("/v1/reports/getJobCosting", reqBody);
  if (status !== 200) {
    throw data;
  }
  return data;
};

export const printOrSaveLotCostingReport = async (reportData, format, action) => {
  const reqBody = {
    org_id: getSelectedOrgId(store.getState()),
    format,
    ...reportData
  };
  const response = await axios.post("/v1/reports/downloadLotCostingReport", reqBody, { responseType: "arraybuffer" });
  if (response.status !== 200) {
    console.log("Error", response.data);
    return;
  }
  printOrSaveBlobFromServer(response, action);
};

export const markAcmBalAck = async (acmId, ack) => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
    acm_id: acmId,
    ack,
  };

  const { status, data } = await axios.post("/v1/markAcmBalAck", reqBody);
  if (status !== 200) {
    throw data;
  }
};

export const resetAcmBalAck = async () => {
  const state = store.getState();
  const reqBody = {
    org_id: getSelectedOrgId(state),
    fy_id: getSelectedFcYearId(state),
  };

  const { status, data } = await axios.post("/v1/resetAcmBalAck", reqBody);
  if (status !== 200) {
    throw data;
  }
};